import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import Question from '../../components/Question';

// Import rune images
import dagazImage from '../../../img/runes/image-023.webp';
import othalaImage from '../../../img/runes/image-023.webp';
import ingwazImage from '../../../img/runes/image-021.webp';

const RunesAdvancedLesson = ({ 
  level = 'beginner', 
  onNextLesson, 
  markLessonComplete,
  currentQuestion = 0,
  onQuestionChange,
  currentLesson,
  completedQuestions = [],
  setCompletedQuestions
}) => {
  const sections = [
    {
      id: 'nine',
      title: 'I. Nine Rune Grid',
      content: `
        The nine rune grid is a powerful and complex spread that provides comprehensive insight into a situation. 
        It arranges nine runes in a 3x3 grid, with each position representing different aspects of the question.
        
        The center rune represents the core issue or the querent's current situation. 
        The surrounding runes provide context, influences, and potential outcomes. 
        This spread can reveal hidden factors and complex dynamics that simpler spreads might miss.
        
        Due to its complexity, this spread is best used for significant questions or situations that require deep analysis. 
        It requires considerable practice and understanding of runic interactions to interpret effectively.
      `,
      imageUrl: dagazImage,
      questions: {
        beginner: {
          question: "How many runes are used in the nine rune grid?",
          options: ["7", "8", "9", "10"],
          correctAnswer: "9",
        },
        intermediate: {
          question: "What does the center rune in the nine rune grid represent?",
          options: [
            "The core issue or current situation",
            "The past",
            "The future",
            "External influences"
          ],
          correctAnswer: "The core issue or current situation",
        },
      },
    },
    {
      id: 'cross',
      title: 'II. Runic Cross Spread',
      content: `
        The runic cross spread is inspired by the Celtic cross but adapted for runic divination. 
        It uses five runes arranged in a cross pattern, with one rune in the center and four surrounding it.
        
        The center rune represents the present situation or the heart of the matter. 
        The top rune represents what covers or influences the situation, the bottom rune shows the foundation or root cause, 
        the left rune represents the past, and the right rune shows the future or potential outcome.
        
        This spread provides a good balance between complexity and manageability, 
        making it suitable for intermediate practitioners who want more depth than a three-rune spread.
      `,
      imageUrl: othalaImage,
      questions: {
        beginner: {
          question: "How many runes are used in the runic cross spread?",
          options: ["3", "5", "7", "9"],
          correctAnswer: "5",
        },
        intermediate: {
          question: "What does the top rune in the cross represent?",
          options: [
            "What covers or influences the situation",
            "The past",
            "The future",
            "The foundation"
          ],
          correctAnswer: "What covers or influences the situation",
        },
      },
    },
    {
      id: 'wheel',
      title: 'III. Runic Wheel Spread',
      content: `
        The runic wheel spread arranges runes in a circular pattern, often using eight runes to represent the eight directions 
        or aspects of life. This spread is particularly useful for questions about life direction, cycles, or comprehensive life readings.
        
        Each position in the wheel represents a different area of life or aspect of the question. 
        The wheel can be read clockwise, starting from the top, or the runes can be interpreted in relation to their positions and each other.
        
        This spread emphasizes the cyclical nature of life and the interconnectedness of different aspects of our experience. 
        It's especially powerful for questions about personal growth and life transitions.
      `,
      imageUrl: ingwazImage,
      questions: {
        beginner: {
          question: "How many runes are typically used in the runic wheel spread?",
          options: ["6", "7", "8", "9"],
          correctAnswer: "8",
        },
        intermediate: {
          question: "What does the wheel spread emphasize?",
          options: [
            "The cyclical nature of life",
            "Linear progression",
            "Random events",
            "External forces"
          ],
          correctAnswer: "The cyclical nature of life",
        },
      },
    },
    {
      id: 'interpretation',
      title: 'IV. Advanced Interpretation Techniques',
      content: `
        Advanced runic interpretation involves understanding complex interactions between multiple runes. 
        Look for patterns, themes, and relationships that emerge when several runes are present together.
        
        Consider the elemental associations of runes, their positions in the aettir (families), and how they relate to each other. 
        Some runes may amplify each other's meanings, while others might create tension or conflict.
        
        Pay attention to the overall energy of the spread - is it predominantly positive, challenging, or balanced? 
        This can provide important context for interpreting individual runes within the larger pattern.
      `,
      imageUrl: dagazImage,
      questions: {
        beginner: {
          question: "What should you look for in advanced interpretation?",
          options: ["Patterns and relationships", "Only individual meanings", "Only the first rune", "Only the last rune"],
          correctAnswer: "Patterns and relationships",
        },
        intermediate: {
          question: "What should you consider about rune relationships?",
          options: [
            "How they amplify or create tension",
            "Only their individual meanings",
            "Only their historical origins",
            "Only their phonetic values"
          ],
          correctAnswer: "How they amplify or create tension",
        },
      },
    },
    {
      id: 'practice',
      title: 'V. Mastering Advanced Spreads',
      content: `
        Mastering advanced spreads requires patience, practice, and a solid foundation in basic runic work. 
        Don't rush into complex spreads before you're comfortable with simpler techniques.
        
        When learning advanced spreads, start by practicing with questions you already have some insight into. 
        This helps you develop your interpretive skills while having a reference point for accuracy.
        
        Remember that complexity doesn't always mean better results. Sometimes a simple three-rune spread can provide 
        all the guidance you need. Choose the spread that feels right for your question and your current level of skill.
      `,
      imageUrl: othalaImage,
      questions: {
        beginner: {
          question: "What should you have before attempting advanced spreads?",
          options: ["A solid foundation in basic work", "Expensive rune sets", "Perfect memory", "Religious beliefs"],
          correctAnswer: "A solid foundation in basic work",
        },
        intermediate: {
          question: "What should you practice with when learning advanced spreads?",
          options: [
            "Questions you already have insight into",
            "Only difficult questions",
            "Only questions about the future",
            "Only questions about others"
          ],
          correctAnswer: "Questions you already have insight into",
        },
      },
    },
  ];

  const [selectedOption, setSelectedOption] = useState('');
  const [imageVisible, setImageVisible] = useState(true);
  const [imageSize, setImageSize] = useState('smallimage');
  const [allQuestionsAnsweredCorrectly, setAllQuestionsAnsweredCorrectly] = useState(false);

  useEffect(() => {
    if (allQuestionsAnsweredCorrectly) {
      markLessonComplete();
      onNextLesson();
    }
  }, [allQuestionsAnsweredCorrectly, markLessonComplete, onNextLesson]);

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      const newCompletedQuestions = [...completedQuestions];
      newCompletedQuestions[currentQuestion] = true;
      setCompletedQuestions(newCompletedQuestions);
      
      if (currentQuestion + 1 < sections.length) {
        onQuestionChange(currentQuestion + 1);
      } else {
        setAllQuestionsAnsweredCorrectly(true);
      }
    }
  };

  const toggleImageVisibility = () => {
    setImageVisible(!imageVisible);
  };

  const toggleImageSize = () => {
    setImageSize(imageSize === 'smallimage' ? 'largeimage' : 'smallimage');
  };

  const getImageStyle = () => {
    const baseStyle = {
      height: 'auto',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.3s ease-in-out'
    };

    if (imageSize === 'smallimage') {
      return {
        ...baseStyle,
        maxWidth: '150px',
        width: '150px'
      };
    } else {
      return {
        ...baseStyle,
        maxWidth: '300px',
        width: '300px'
      };
    }
  };

  const transition = { duration: 0.15, ease: "easeInOut" };

  return (
    <div>
      <Helmet>
        <title>Advanced Divination Spreads - {level.charAt(0).toUpperCase() + level.slice(1)} Lesson</title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          {imageVisible && sections[currentQuestion]?.imageUrl && (
            <div className="has-text-centered mb-4">
              <img
                src={sections[currentQuestion].imageUrl}
                alt={`Advanced Runic Spreads - ${sections[currentQuestion].title}`}
                style={getImageStyle()}
              />
              <div className="icon-buttons mt-2">
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  className="icon-button has-text-warning mr-3"
                  onClick={toggleImageVisibility}
                  style={{ cursor: 'pointer' }}
                />
                <FontAwesomeIcon
                  icon={faExpandArrowsAlt}
                  className="icon-button has-text-warning"
                  onClick={toggleImageSize}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>
          )}

          <section className="section has-text-centered" id={sections[currentQuestion]?.id}>
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <h2 className="has-text-primary">{sections[currentQuestion]?.title}</h2>
                  <p className="has-text-white">{sections[currentQuestion]?.content}</p>
                </div>
              </div>
            </div>
          </section>

          {sections[currentQuestion]?.questions && (
            <section className="section has-text-centered">
              <div className="container content">
                <div className="columns">
                  <div className="column">
                    <Question
                      question={sections[currentQuestion].questions[level].question}
                      options={sections[currentQuestion].questions[level].options}
                      correctAnswer={sections[currentQuestion].questions[level].correctAnswer}
                      onAnswered={handleAnswer}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default RunesAdvancedLesson; 